import { useTodayNutritionRoll } from "@/hooks/use-nutrition-roll";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";

/**
 * BW9 — Macros ring widget for /health/nutrition.
 *
 * Center: kcal vs target. Three concentric arc segments for protein, carbs,
 * fat — each scaled against the daily target derived from current_weight_kg.
 * Values are pulled from `nutrition_roll` (recomputed by the meal trigger).
 *
 * If no profile weight is set, falls back to a 2400 kcal target so the ring
 * still renders. The macro grams target uses a simple split:
 *   protein 1.8 g/kg, fat 1.0 g/kg, carbs balance.
 */

const SIZE = 200;
const STROKE = 14;
const GAP = 4;

export function MacrosRing() {
  const roll = useTodayNutritionRoll();
  const profile = useAthleteProfile();
  const weight = profile.data?.current_weight_kg ?? null;
  const kcalTarget = weight ? Math.round(weight * 30) : 2400;
  const proteinTarget = weight ? Math.round(weight * 1.8) : 130;
  const fatTarget = weight ? Math.round(weight * 1.0) : 75;
  const carbsTarget = Math.round((kcalTarget - proteinTarget * 4 - fatTarget * 9) / 4);

  const r = roll.data;
  const kcalPct = r ? Math.min(1, r.kcal_total / kcalTarget) : 0;
  const proteinPct = r ? Math.min(1, r.protein_g / proteinTarget) : 0;
  const carbsPct = r ? Math.min(1, r.carbs_g / carbsTarget) : 0;
  const fatPct = r ? Math.min(1, r.fat_g / fatTarget) : 0;

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "var(--space-4)",
        alignItems: "center",
      }}
    >
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
        {/* outer ring — kcal */}
        <ProgressRing
          radius={(SIZE - STROKE) / 2}
          stroke={STROKE}
          color="var(--accent)"
          track="var(--bg-3)"
          pct={kcalPct}
        />
        {/* protein ring */}
        <ProgressRing
          radius={(SIZE - STROKE) / 2 - (STROKE + GAP)}
          stroke={STROKE - 4}
          color="var(--macro-protein)"
          track="var(--bg-3)"
          pct={proteinPct}
        />
        {/* carbs ring */}
        <ProgressRing
          radius={(SIZE - STROKE) / 2 - 2 * (STROKE + GAP)}
          stroke={STROKE - 4}
          color="var(--macro-carbs)"
          track="var(--bg-3)"
          pct={carbsPct}
        />
        {/* fat ring — steel-blue to stay visually distinct from accent
         * verdigris on both dark and cream surfaces */}
        <ProgressRing
          radius={(SIZE - STROKE) / 2 - 3 * (STROKE + GAP)}
          stroke={STROKE - 4}
          color="var(--macro-fat)"
          track="var(--bg-3)"
          pct={fatPct}
        />
        {/* center label */}
        <text
          x={SIZE / 2}
          y={SIZE / 2 - 4}
          textAnchor="middle"
          fontFamily="var(--font-sans)"
          fontSize="22"
          fontWeight="500"
          fill="var(--fg)"
        >
          {Math.round(r?.kcal_total ?? 0)}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 16}
          textAnchor="middle"
          fontFamily="var(--font-sans)"
          fontSize="11"
          fill="var(--fg-3)"
        >
          / {kcalTarget} kcal
        </text>
      </svg>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-2)" }}>
        <Legend color="var(--macro-protein)" label="Protein" value={r?.protein_g ?? 0} target={proteinTarget} unit="g" />
        <Legend color="var(--macro-carbs)" label="Carbs" value={r?.carbs_g ?? 0} target={carbsTarget} unit="g" />
        <Legend color="var(--macro-fat)" label="Fat" value={r?.fat_g ?? 0} target={fatTarget} unit="g" />
        <Legend color="var(--fg-3)" label="Fiber" value={r?.fiber_g ?? 0} target={null} unit="g" />
        <li style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", marginTop: "var(--space-1)" }}>
          {(r?.meals_logged ?? 0)} meal{(r?.meals_logged ?? 0) === 1 ? "" : "s"} today
        </li>
      </ul>
    </article>
  );
}

function ProgressRing({
  radius,
  stroke,
  color,
  track,
  pct,
}: {
  radius: number;
  stroke: number;
  color: string;
  track: string;
  pct: number;
}) {
  const circ = 2 * Math.PI * radius;
  return (
    <g transform={`translate(${SIZE / 2} ${SIZE / 2}) rotate(-90)`}>
      <circle r={radius} fill="none" stroke={track} strokeWidth={stroke} />
      <circle
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${circ * pct} ${circ}`}
        strokeLinecap="round"
      />
    </g>
  );
}

function Legend({
  color,
  label,
  value,
  target,
  unit,
}: {
  color: string;
  label: string;
  value: number;
  target: number | null;
  unit: string;
}) {
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: "10px 1fr auto",
        gap: 8,
        alignItems: "baseline",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
      }}
    >
      <span
        aria-hidden
        style={{ width: 10, height: 10, borderRadius: "50%", background: color, display: "inline-block" }}
      />
      <span>{label}</span>
      <span className="mono" style={{ color: "var(--fg)" }}>
        {Math.round(value)}
        {target ? <span style={{ color: "var(--fg-3)" }}>{` / ${target}${unit}`}</span> : unit}
      </span>
    </li>
  );
}
